import React, { useMemo } from 'react';
import { FiClock } from 'react-icons/fi';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTranslation } from '../../contexts/TranslationContext';
import { TimePeriod } from './ExpertRoiTimelineChart';

export interface BreakdownItem {
  id: number;
  label: string;
  count: number;
  won: number;
  roi?: number | null;
}

function fmtDateRange(from: Date, to: Date): string {
  const fromStr = from.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const toStr = to.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${fromStr} — ${toStr}`;
}

interface Props {
  sportBreakdown: BreakdownItem[];
  leagueBreakdown: BreakdownItem[];
  sportFiltered: boolean;
  timePeriod?: TimePeriod;
  onTimePeriodChange?: (period: TimePeriod) => void;
  fromDate?: Date;
  toDate?: Date;
}

const COLORS = {
  primary: '#58C9FA',
  win: '#3fb950',
  grid: 'rgba(240,242,245,0.08)',
  textMuted: '#8B949E',
  barBg: 'rgba(88,201,250,0.08)',
};

function truncate(str: string, max: number): string {
  return str.length > max ? str.slice(0, max - 1) + '…' : str;
}

const ExpertLeagueSportRecommendationsBarChart: React.FC<Props> = ({
  sportBreakdown,
  leagueBreakdown,
  sportFiltered,
  timePeriod,
  onTimePeriodChange,
  fromDate,
  toDate,
}) => {
  const { t } = useTranslation();

  const periodOptions = useMemo(
    (): { value: TimePeriod; label: string }[] => [
      { value: '7d', label: t.charts.period7d },
      { value: '30d', label: t.charts.period30d },
      { value: '3m', label: t.charts.period3m },
      { value: 'ytd', label: t.charts.periodYtd },
      { value: 'all', label: t.charts.periodAll },
    ],
    [t],
  );

  const data = sportFiltered ? leagueBreakdown : sportBreakdown;

  const sortedByCount = data
    .slice()
    .sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.label.localeCompare(b.label);
    });
  const top = sortedByCount[0];
  const totalPicks = data.reduce((sum, x) => sum + x.count, 0);
  const topPct =
    totalPicks > 0 && top ? Math.round((top.count / totalPicks) * 1000) / 10 : 0;

  const chartData = sortedByCount.map(item => ({
      ...item,
      label: truncate(item.label, 18),
      lost: item.count - item.won,
    }));

  const insightHint = sportFiltered
    ? t.expertStats.ofPicksInLeague.replace('{label}', top?.label ?? '')
    : t.expertStats.ofPicksAreSport.replace('{label}', top?.label ?? '');

  return (
    <div className="breakdown-bar-chart">
      {top && totalPicks > 0 && (
        <div className="roi-chart__header breakdown-bar-chart__insight">
          <span className="roi-chart__cash" style={{ color: COLORS.primary }}>
            {topPct.toFixed(topPct % 1 === 0 ? 0 : 1)}%
          </span>
          <span className="roi-chart__hint">{insightHint}</span>
        </div>
      )}

      {fromDate && toDate && timePeriod && (
        <div className="roi-chart__meta">
          <div className="roi-chart__dates">
            <FiClock size={12} />
            <span>{fmtDateRange(fromDate, toDate)}</span>
          </div>
          <div className="roi-chart__period-filter">
            {periodOptions.map(({ value, label }) => (
              <button
                key={value}
                className={`roi-chart__period-btn${timePeriod === value ? ' roi-chart__period-btn--active' : ''}`}
                onClick={() => onTimePeriodChange?.(value)}
                type="button"
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {chartData.length === 0 ? (
        <div className="breakdown-bar-chart__empty">{t.expertStats.noDataForPeriod}</div>
      ) : (
        <ResponsiveContainer width="100%" height={240}>
          <BarChart
            data={chartData}
            margin={{ top: 4, right: 4, left: -20, bottom: 0 }}
            barSize={chartData.length > 6 ? 12 : 22}
          >
            <CartesianGrid vertical={false} stroke={COLORS.grid} />
            <XAxis
              dataKey="label"
              tick={{ fill: COLORS.textMuted, fontSize: 11 }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis hide allowDecimals={false} />
            <Tooltip
              contentStyle={{
                background: '#161B22',
                border: '1px solid rgba(240,242,245,0.12)',
                borderRadius: 8,
                fontSize: 12,
                color: '#F0F2F5',
              }}
              cursor={{ fill: COLORS.barBg }}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              formatter={(value: any, name: any) => [
                value,
                name === 'won' ? t.pickResults.won : t.pickResults.lost,
              ] as [number, string]}
            />
            <Bar dataKey="won" stackId="a" fill={COLORS.win} />
            <Bar dataKey="lost" stackId="a" fill={COLORS.primary} fillOpacity={0.35} radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default ExpertLeagueSportRecommendationsBarChart;
